import '@/styles/globals.scss';
import '@fontsource-variable/funnel-display/wght.css';
import '@fontsource-variable/geist/wght.css';
import '@fontsource/pt-sans-narrow/latin-400.css';
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { env } from '@/lib/env';

import App from './App';

if (env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    release: env.VITE_RELEASE,
    tracesSampleRate: 0.1,
    integrations: [Sentry.browserTracingIntegration()],
  });
}

// Starts the in-browser MSW worker so flows can be exercised without a backend.
// Gated solely on the flags (not import.meta.env.DEV) so it also works in a
// production build — QA, where the backend isn't deployed yet. VITE_MOCK_API
// mocks the whole backend (auth + feature APIs, seeded); VITE_MOCK_AUTH mocks
// only /auth/*. Keep both flags unset everywhere except QA, and remove them once
// the backend is live. The worker chunk is dynamically imported, so nothing ships
// unless a flag is on.
async function enableMockBackend() {
  const mockApi = import.meta.env.VITE_MOCK_API === 'true';
  const mockAuth = import.meta.env.VITE_MOCK_AUTH === 'true';
  if (!mockApi && !mockAuth) return;

  const { worker, seedMockBackend } = await import('@/test/msw/browser');
  if (mockApi) seedMockBackend();
  await worker.start({ onUnhandledRequest: 'bypass' });
}

await enableMockBackend();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
